import { create } from 'zustand';
import { GamePackage, Track } from '../types';
import { getAuthToken } from '../services/authService';
import { getApiUrl } from '../services/apiBase';

/**
 * Store для управления пакетами игр
 * Работает с API сервера
 */
interface AdminStore {
  packages: GamePackage[];
  isLoading: boolean;
  error: string | null;
  addPackage: (pkg: GamePackage) => Promise<void>;
  updatePackage: (id: string, pkg: Partial<GamePackage>) => Promise<void>;
  deletePackage: (id: string) => Promise<void>;
  loadPackages: () => Promise<void>;
}

const API_URL = getApiUrl();

// Функция перемешивания массива (алгоритм Фишера-Йетса)
const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const useAdminStore = create<AdminStore>((set) => ({
  packages: [],
  isLoading: false,
  error: null,

  // Загрузка пакетов с сервера
  loadPackages: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_URL}/packages`);
      if (!response.ok) {
        throw new Error('Failed to load packages');
      }
      const packages = await response.json();
      set({ packages, isLoading: false });
    } catch (error) {
      console.error('Ошибка загрузки пакетов:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false 
      });
    }
  },

  // Добавление нового пакета
  addPackage: async (pkg: GamePackage) => {
    set({ isLoading: true, error: null });
    try {
      const token = getAuthToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/packages`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(pkg),
      });
      if (!response.ok) {
        throw new Error('Failed to create package');
      }
      const newPackage = await response.json();
      set((state) => ({
        packages: [...state.packages, newPackage],
        isLoading: false,
      }));
    } catch (error) {
      console.error('Ошибка создания пакета:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false 
      });
      throw error;
    }
  },

  // Обновление пакета
  updatePackage: async (id: string, updates: Partial<GamePackage>) => {
    set({ isLoading: true, error: null });
    try {
      const token = getAuthToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/packages/${id}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        throw new Error('Failed to update package');
      }
      const updatedPackage = await response.json();
      set((state) => ({
        packages: state.packages.map((pkg) =>
          pkg.id === id ? updatedPackage : pkg
        ),
        isLoading: false,
      }));
    } catch (error) {
      console.error('Ошибка обновления пакета:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false 
      });
      throw error;
    }
  },

  // Удаление пакета
  deletePackage: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const token = getAuthToken();
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/packages/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to delete package');
      }
      set((state) => ({
        packages: state.packages.filter((pkg) => pkg.id !== id),
        isLoading: false,
      }));
    } catch (error) {
      console.error('Ошибка удаления пакета:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false 
      });
      throw error;
    }
  },
}));

// Функция для перемешивания треков и создания карты номеров
export const shuffleTracks = (tracks: Track[]): Map<number, Track> => {
  const shuffled = shuffleArray(tracks);
  const map = new Map<number, Track>();
  shuffled.forEach((track, index) => {
    map.set(index + 1, { ...track, number: index + 1 });
  });
  return map;
};

